import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FlexBox from './FlexBox';
import { Span } from './Typography';
import ToastNotification from './ToastNotification';
import 'pure-react-carousel/dist/react-carousel.es.css';
import IncDecButton from './IncDecButton';
import Dropdown from './Dropdown';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import React, { useState, useContext } from 'react';
import { GlobalContext } from '../context/GlobalState';
import { languages, currency } from 'constants';
import { strings } from 'localisation';
import createGAevent from 'helpers';
import fs from 'fs';
import { variationPriceSx, variationCurrencySx } from './ProductViewGridItemIntro';
import {
  expandHeightAttributes,
  findHeightOptionForCm,
  resolveHeightPreselect,
  TUCK_ALLOWANCE_CM
} from '../utils/heightUtils';

// Heading shown above the size dropdowns, keyed by subCategoryId (ids come from /api/menu).
// Fitted and round sheets are sized by the mattress; everything else by the item itself.
// This is the only place these strings live - edit them here.
const SIZE_HEADING_BY_SUBCATEGORY = {
  1: 'Изберете размера на чаршафа:', // Чаршафи
  2: 'Изберете размера на калъфката:', // Калъфки за възглавници
  3: 'Изберете размера на Вашия матрак:', // Чаршафи с ластик
  4: 'Изберете размера на плика:', // Пликове за завивка
  6: 'Изберете размера на Вашия матрак:', // Кръгли чаршафи с ластик
  7: 'Изберете размера на чаршафа:', // Чаршафи-KIDS
  8: 'Изберете размера на калъфката:', // Калъфки за възглавници-KIDS
  9: 'Изберете размер:', // Чаршафи с ластик-KIDS
  10: 'Изберете размера на плика:' // Пликове за завивка-KIDS
};

// A duvet cover is made to the size the customer picks here, not to the duvet they own. Matched
// exactly, it ends up tight once the fabric shrinks, so the size heading carries a note.
// Keyed by subCategoryId (ids come from /api/menu).
const DUVET_COVER_SUBCATEGORIES = [
  4, // Пликове за завивка
  10 // Пликове за завивка-KIDS
];

const DUVET_COVER_SIZE_NOTE = {
  bg: 'Препоръчваме да изберете размер на плика малко по-голям от този на вашата завивка.',
  en: 'We recommend choosing a cover slightly larger than your duvet.'
};

// Fitted sheets are cut to the mattress height chosen in the dropdowns above, so the customer also
// has to say whether we add the allowance that lets the sheet tuck underneath. Keyed by
// subCategoryId (ids come from /api/menu).
const TUCK_ALLOWANCE_SUBCATEGORIES = [
  3, // Чаршафи с ластик
  6 // Кръгли чаршафи с ластик
];

const TUCK_ALLOWANCE_COPY = {
  question: {
    bg: 'Желаете ли допълнителни 5 см към височината за подпъхване под матрака?',
    en: 'Would you like additional 5 cm to the height for tucking under the mattress?'
  },
  placeholder: {
    bg: 'Изберете опция',
    en: 'Choose an option'
  },
  yes: {
    bg: 'Да, добавете 5 см',
    en: 'Yes, add 5 cm'
  },
  no: {
    bg: 'Не, не желая',
    en: 'No, I do not want'
  },
  hint: {
    bg: 'Допълнителните 5 см осигуряват сигурно и равномерно подпъхване под матрака от всички страни.',
    en: 'The extra 5 cm ensure secure and even tucking under the mattress from all sides.'
  }
};

// The "unit x quantity = line total" EUR figures round per unit before multiplying, matching the
// cart, checkout summary and order email.
const quantityLineFigures = (unitEUR, quantity) => {
  const price = parseFloat(unitEUR);
  return {
    code: currency.EUR,
    unit: price.toFixed(2),
    total: (price * quantity).toFixed(2)
  };
};

export default function ProductViewGridItemAction({
  product,
  additionalProductData,
  variationPrice,
  setVariationPrice
}) {
  const params = useParams();
  const { addCartItem, lang } = useContext(GlobalContext);

  // Dimension values arriving from a filtered listing link (e.g. ?width=180) - used to preselect the
  // matching dropdown(s). The size dimension arrives as `sizeValue` (see Products.jsx / backend note).
  const location = useLocation();
  const dimSearch = new URLSearchParams(location.search);
  const preselectWidth = dimSearch.get('width');
  const preselectHeight = dimSearch.get('height');
  const preselectLength = dimSearch.get('length');
  const preselectSize = dimSearch.get('sizeValue');

  const [isATCtoastOpen, setATCtoastOpen] = React.useState(false);
  const [isAlertOpenNoOptions, setIsAlertOpenNoOptions] = React.useState(false);
  const [isAlertOpenOperation, setIsAlertOpenOperation] = React.useState(false);
  const [attributesArray, setAttributesArray] = React.useState({});
  const [quantity, setQuantity] = React.useState(1);
  const [variationPriceId, setVariationPriceId] = useState(null);
  const [tuckAllowance, setTuckAllowance] = React.useState('');

  const notificationATCMessage = strings[lang].nitificationForAddedToCart;
  const buttonLabelATC = strings[lang].addToCartButton;

  let attributesCount = 0;

  const sizeHeading = SIZE_HEADING_BY_SUBCATEGORY[Number(product.subCategoryId)];
  const isDuvetCover = DUVET_COVER_SUBCATEGORIES.includes(Number(product.subCategoryId));

  if (additionalProductData.heightAttributes && additionalProductData.heightAttributes.length > 0) {
    attributesCount += 1;
  }
  if (additionalProductData.widthAttributes && additionalProductData.widthAttributes.length > 0) {
    attributesCount += 1;
  }
  if (additionalProductData.lengthAttributes && additionalProductData.lengthAttributes.length > 0) {
    attributesCount += 1;
  }
  if (additionalProductData.sizeAttributes && additionalProductData.sizeAttributes.length > 0) {
    attributesCount += 1;
  }

  // Heights are stored as price ranges ("10-15"); the dropdown offers each centimetre instead, so a
  // customer can pick 13 without working out which bucket that is. See utils/heightUtils.
  const heightOptions = React.useMemo(
    () => expandHeightAttributes(additionalProductData.heightAttributes),
    [additionalProductData.heightAttributes]
  );

  // The tuck allowance is a required answer, not a dimension: it never becomes its own price lookup
  // key, but it does move the height we price on (see pricedHeightAttributeId). Nothing is priced or
  // added to the cart until the customer has answered it.
  const requiresTuckChoice = TUCK_ALLOWANCE_SUBCATEGORIES.includes(Number(product.subCategoryId));
  const allDimensionsChosen =
    attributesCount > 0 && attributesCount === Object.keys(attributesArray).length;
  const allOptionsChosen = allDimensionsChosen && (!requiresTuckChoice || tuckAllowance !== '');

  const tuckCopy = (key) =>
    languages.bg === lang ? TUCK_ALLOWANCE_COPY[key].bg : TUCK_ALLOWANCE_COPY[key].en;

  const addsTuckAllowance = requiresTuckChoice && tuckAllowance === 'yes';

  // A tucked sheet is cut to mattress height + 5 cm, so it is priced on that taller height: a 13 cm
  // mattress with the allowance is an 18 cm sheet, which sits in the 16-20 range, not 10-15. The
  // customer keeps seeing (and the order records) the 13 cm they chose.
  const pricedHeightAttributeId = (chosenHeight) => {
    if (!addsTuckAllowance) {
      return chosenHeight.id;
    }
    const priced = findHeightOptionForCm(
      heightOptions,
      Number(chosenHeight.value) + TUCK_ALLOWANCE_CM
    );
    return priced ? priced.id : chosenHeight.id;
  };

  const addItemToCart = () => {
    let cartItemObj = {};
    cartItemObj['id'] = additionalProductData.id;
    cartItemObj['quantity'] = quantity;
    cartItemObj['price'] = variationPrice;
    cartItemObj['currency'] = currency.EUR;
    cartItemObj['priceId'] = variationPriceId;
    cartItemObj['imgUrl'] = product.images[0].url;
    // Snapshot, not a live reference: the cart line must keep the size that was chosen when it was
    // added, even if the customer goes on to pick a different one on this same page.
    cartItemObj['attributes'] = { ...attributesArray };
    cartItemObj['slug_bg'] = product.slug_bg;

    // Travels to /api/makeOrder so the order email can note the added 5 cm next to the mattress
    // height the customer chose.
    if (requiresTuckChoice) {
      cartItemObj['tuckAllowance'] = tuckAllowance;
    }

    if (languages.bg === lang) {
      cartItemObj['name'] = product.name_bg;
    }

    let cartItem = [];
    cartItem.push(cartItemObj);

    if (allOptionsChosen && variationPrice != null) {
      addCartItem(cartItem);
      handleOpenNotification();
      createGAevent(
        'Product',
        'ADD_PRODUCT_TO_CART_EVENT',
        'productId-' +
          additionalProductData.id +
          '---priceId-' +
          variationPriceId +
          '---qty-' +
          quantity
      );

      createGAevent(
        'Product',
        `ADD_PRODUCT_TO_CART-productId-${additionalProductData.id}-priceId-${variationPriceId}-qty-${quantity}`,
        'productId-' +
          additionalProductData.id +
          '---priceId-' +
          variationPriceId +
          '---qty-' +
          quantity
      );
    } else if (allOptionsChosen && variationPrice == null) {
      setIsAlertOpenOperation(true);
    } else {
      setIsAlertOpenNoOptions(true);
    }
  };

  const handleOpenNotification = () => {
    setATCtoastOpen(true);
  };
  const handleCloseNotification = () => {
    setATCtoastOpen(false);
  };

  const handleCloseAlertNoOptions = () => {
    setIsAlertOpenNoOptions(false);
  };

  const handleCloseAlertOperation = () => {
    setIsAlertOpenOperation(false);
  };

  React.useEffect(() => {
    if (allOptionsChosen) {
      getVariationPrice(attributesArray, additionalProductData.id);
    }
  }, [attributesArray, tuckAllowance]);

  const getVariationPrice = async (attributes, productId) => {
    if (productId != null) {
      params['productId'] = productId;
    }
    if ('width' in attributes) {
      params['widthAttributeId'] = attributes.width.id;
    }
    if ('length' in attributes) {
      params['lengthAttributeId'] = attributes.length.id;
    }
    if ('height' in attributes) {
      params['heightAttributeId'] = pricedHeightAttributeId(attributes.height);
    }
    if ('size' in attributes) {
      params['sizeAttributeId'] = attributes.size.id;
    }
    const { data: response } = await axios
      .get(`/price/?`, {
        params
      })
      .catch((error) => {
        let errorMessage = {
          error:
            '--------------------------------------------------------------------------' +
            new Date() +
            '-  Error while trying to getVariationPrice!!!',
          error_log: error
        };
        fs.appendFile(
          'error_logs.json',
          JSON.stringify(errorMessage) + '---------------------------------------------------------'
        );
      });
    if (response.length > 0 && response[0].price_eur) {
      setVariationPrice(response[0].price_eur);
      setVariationPriceId(response[0].id);
    } else if (response.length === 0) {
      setVariationPrice(null);
    }
  };

  const attributeLabelLocalised = {
    length: {
      bg: 'дължина на матрака (см)',
      en: 'lmattress length'
    },
    width: {
      bg: 'ширина на матрака (см)',
      en: 'mattress width'
    },
    length_duvet_cover: {
      bg: 'дължина (см)',
      en: 'length'
    },
    width_duvet_cover: {
      bg: 'ширина (см)',
      en: 'width'
    },
    height: {
      bg: 'височина на матрака (см)',
      en: 'mattress height'
    },
    size_pillowcase: {
      bg: 'размер (см)',
      en: 'size'
    },
    size: {
      bg: 'размер (см) - ширина x дължина ',
      en: 'size - width x length'
    },
    size_kids_fitted: {
      bg: 'размер (см) - Ш х Д х В',
      en: 'size'
    },
    length_round: {
      bg: 'диаметър на матрака (см)',
      en: 'mattress diameter'
    }
  };

  // Only read once a variation is priced, which is also the only time the line below renders.
  const lineFigures = quantityLineFigures(variationPrice, quantity);

  return (
    <Grid
      item
      xs={12}
      sm={3}
      style={{
        backgroundColor: ''
        // marginLeft: 1
        // display:'flex',
        // flexDirection:'column',
      }}>
      <ToastNotification
        open={isAlertOpenNoOptions}
        handleCloseNotification={handleCloseAlertNoOptions}
        type="alert"
        position="top-center"
        bckgrColor="#B71C1C"
        textColor="white"
        message="Моля, първо изберете опции на продукта преди да го добавите в количката."
      />

      <ToastNotification
        open={isAlertOpenOperation}
        handleCloseNotification={handleCloseAlertOperation}
        type="alert"
        position="top-center"
        bckgrColor="#B71C1C"
        textColor="white"
        message="За съжаление тази операция не може да бъде изпълнена. Моля, опитайте отното като изберете други параметри за продукта."
      />

      <ToastNotification
        open={isATCtoastOpen}
        handleCloseNotification={handleCloseNotification}
        time="2000"
        message={notificationATCMessage}
      />
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        }}>
        {attributesCount > 0 && sizeHeading && (
          <Typography
            sx={{
              pt: 1,
              pb: 1,
              fontSize: '18px',
              fontWeight: '700',
              textWrap: 'balance',
              color: 'gold.text',
              textAlign: 'center'
            }}>
            {sizeHeading}
          </Typography>
        )}

        {attributesCount > 0 && isDuvetCover && (
          <Typography
            sx={{
              pb: 2,
              px: 1,
              fontSize: '13px',
              lineHeight: 1.5,
              color: 'text.secondary',
              textAlign: 'center'
            }}>
            {languages.bg === lang ? DUVET_COVER_SIZE_NOTE.bg : DUVET_COVER_SIZE_NOTE.en}
          </Typography>
        )}

        {additionalProductData.widthAttributes &&
          additionalProductData.widthAttributes.length > 0 &&
          product.subCategoryId !== 4 && (
            <Dropdown
              attributeType="width"
              attributeLabel={
                languages.bg === lang
                  ? attributeLabelLocalised['width'].bg
                  : attributeLabelLocalised['width'].en
              }
              values={additionalProductData.widthAttributes}
              attributesArray={attributesArray}
              setAttributesArrayFunc={setAttributesArray}
              preselectValue={preselectWidth}
            />
          )}

        {additionalProductData.widthAttributes &&
          additionalProductData.widthAttributes.length > 0 &&
          product.subCategoryId == 4 && (
            <Dropdown
              attributeType="width"
              attributeLabel={
                languages.bg === lang
                  ? attributeLabelLocalised['width_duvet_cover'].bg
                  : attributeLabelLocalised['width_duvet_cover'].en
              }
              values={additionalProductData.widthAttributes}
              attributesArray={attributesArray}
              setAttributesArrayFunc={setAttributesArray}
              preselectValue={preselectWidth}
            />
          )}

        {additionalProductData.lengthAttributes &&
          additionalProductData.lengthAttributes.length > 0 &&
          product.subCategoryId !== 6 &&
          product.subCategoryId == 4 && (
            <Dropdown
              attributeType="length"
              attributeLabel={
                languages.bg === lang
                  ? attributeLabelLocalised['length_duvet_cover'].bg
                  : attributeLabelLocalised['length_duvet_cover'].en
              }
              values={additionalProductData.lengthAttributes}
              attributesArray={attributesArray}
              setAttributesArrayFunc={setAttributesArray}
              preselectValue={preselectLength}
            />
          )}

        {additionalProductData.lengthAttributes &&
          additionalProductData.lengthAttributes.length > 0 &&
          product.subCategoryId !== 6 &&
          product.subCategoryId !== 4 && (
            <Dropdown
              attributeType="length"
              attributeLabel={
                languages.bg === lang
                  ? attributeLabelLocalised['length'].bg
                  : attributeLabelLocalised['length'].en
              }
              values={additionalProductData.lengthAttributes}
              attributesArray={attributesArray}
              setAttributesArrayFunc={setAttributesArray}
              preselectValue={preselectLength}
            />
          )}

        {additionalProductData.lengthAttributes &&
          additionalProductData.lengthAttributes.length > 0 &&
          product.subCategoryId === 6 &&
          product.subCategoryId !== 4 && (
            <Dropdown
              attributeType="length"
              attributeLabel={
                languages.bg === lang
                  ? attributeLabelLocalised['length_round'].bg
                  : attributeLabelLocalised['length_round'].en
              }
              values={additionalProductData.lengthAttributes}
              attributesArray={attributesArray}
              setAttributesArrayFunc={setAttributesArray}
              preselectValue={preselectLength}
            />
          )}

        {heightOptions && heightOptions.length > 0 && (
          <Dropdown
            attributeType="height"
            attributeLabel={
              languages.bg === lang
                ? attributeLabelLocalised['height'].bg
                : attributeLabelLocalised['height'].en
            }
            values={heightOptions}
            attributesArray={attributesArray}
            setAttributesArrayFunc={setAttributesArray}
            preselectValue={resolveHeightPreselect(
              preselectHeight,
              additionalProductData.heightAttributes
            )}
          />
        )}

        {additionalProductData.sizeAttributes &&
          additionalProductData.sizeAttributes.length > 0 &&
          product.subCategoryId !== 2 &&
          product.subCategoryId !== 8 &&
          product.subCategoryId !== 9 && (
            <Dropdown
              attributeType="size"
              attributeLabel={
                languages.bg === lang
                  ? attributeLabelLocalised['size'].bg
                  : attributeLabelLocalised['size'].en
              }
              values={additionalProductData.sizeAttributes}
              attributesArray={attributesArray}
              setAttributesArrayFunc={setAttributesArray}
              preselectValue={preselectSize}
            />
          )}

        {additionalProductData.sizeAttributes &&
          additionalProductData.sizeAttributes.length > 0 &&
          product.subCategoryId == 9 && (
            <Dropdown
              attributeType="size"
              attributeLabel={
                languages.bg === lang
                  ? attributeLabelLocalised['size_kids_fitted'].bg
                  : attributeLabelLocalised['size_kids_fitted'].en
              }
              values={additionalProductData.sizeAttributes}
              attributesArray={attributesArray}
              setAttributesArrayFunc={setAttributesArray}
              preselectValue={preselectSize}
            />
          )}

        {additionalProductData.sizeAttributes &&
          additionalProductData.sizeAttributes.length > 0 &&
          (product.subCategoryId == 2 || product.subCategoryId == 8) && (
            <Dropdown
              attributeType="size"
              attributeLabel={
                languages.bg === lang
                  ? attributeLabelLocalised['size_pillowcase'].bg
                  : attributeLabelLocalised['size_pillowcase'].en
              }
              values={additionalProductData.sizeAttributes}
              attributesArray={attributesArray}
              setAttributesArrayFunc={setAttributesArray}
              preselectValue={preselectSize}
            />
          )}

        {requiresTuckChoice && (
          <Box sx={{ width: '100%', p: 1, pt: 2 }}>
            <Typography
              id="tuck-allowance-question"
              sx={{
                pb: 1,
                fontSize: '15px',
                fontWeight: '600',
                color: 'text.primary',
                lineHeight: 1.4,
                textAlign: 'center'
              }}>
              {tuckCopy('question')}
            </Typography>
            <FormControl fullWidth>
              <Select
                id="tuck-allowance"
                name="tuck-allowance"
                displayEmpty
                value={tuckAllowance}
                onChange={(event) => setTuckAllowance(event.target.value)}
                aria-labelledby="tuck-allowance-question"
                // The options are full sentences - let them wrap instead of being clipped.
                renderValue={(selected) =>
                  selected === '' ? (
                    <Span color="grey.600">{tuckCopy('placeholder')}</Span>
                  ) : (
                    tuckCopy(selected)
                  )
                }
                sx={{
                  '& .MuiSelect-select': { whiteSpace: 'normal', fontSize: '14px' }
                }}>
                {['yes', 'no'].map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{ whiteSpace: 'normal', fontSize: '14px' }}>
                    {tuckCopy(option)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText
                sx={{ mx: 0, mt: 1, fontSize: '12px', lineHeight: 1.4, textAlign: 'center' }}>
                {tuckCopy('hint')}
              </FormHelperText>
            </FormControl>
          </Box>
        )}

        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}>
          {allOptionsChosen && variationPrice != null && (
            <Box
              sx={{
                p: 1,
                pt: 2,
                display: 'flex',
                alignItems: 'baseline',
                flexWrap: 'wrap',
                columnGap: 1
              }}>
              <Typography component="span" sx={variationPriceSx}>
                {parseFloat(variationPrice).toFixed(2)}{' '}
                <Box component="span" sx={variationCurrencySx}>
                  {currency.EUR}
                </Box>
              </Typography>
            </Box>
          )}

          {quantity > 1 && variationPrice > 0 && (
            <FlexBox
              flexWrap="wrap"
              alignItems="center"
              style={{ paddingTop: 5, paddingBottom: 10 }}>
              <Span color="text.primary" mr={1}>
                {lineFigures.unit} <span style={{ fontWeight: '600' }}>{lineFigures.code}</span> x{' '}
                {quantity} =
              </Span>
              <Span fontWeight={600} color="price.main" mr={2}>
                {lineFigures.total} <span style={{ fontWeight: '600' }}>{lineFigures.code}</span>
              </Span>
            </FlexBox>
          )}

          {attributesCount > 0 && <IncDecButton counter={quantity} setCounter={setQuantity} />}

          {attributesCount > 0 && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mb: 2, mt: 2 }}
              onClick={addItemToCart}>
              {buttonLabelATC}
            </Button>
          )}

          {/* <ModalPopup url={product.sizing_guide} /> */}
        </Box>
      </Box>
    </Grid>
  );
}
